import React, { useState } from 'react';
import { AlertTriangle, Eye, EyeOff, Info } from 'lucide-react';
import { BrandMark } from '../components/layout/Brand';
import { Button } from '../components/ui/Button';
import { useAuth } from '../context/AuthContext';

/** Sign-in screen. After signing in the user lands on the page they asked for. */
const LoginPage = () => {
  const { signIn, message } = useAuth();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!username || !password) {
      setError('Enter your username and password.');
      return;
    }
    setError('');
    setBusy(true);
    try {
      await signIn(username, password);
    } catch (err) {
      setError(err.status === 401 ? 'That username and password combination is not recognised.' : err.message);
      setBusy(false);
    }
  };

  return (
    <main className="relative flex min-h-screen flex-col items-center justify-center bg-canvas px-4 py-12">
      <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden="true">
        <div className="absolute left-1/2 top-1/3 h-[28rem] w-[28rem] -translate-x-1/2 -translate-y-1/2 rounded-full bg-accent/10 blur-[110px]" />
      </div>

      <div className="relative w-full max-w-sm">
        <div className="mb-8 flex flex-col items-center text-center">
          <BrandMark size="lg" />
          <h1 className="mt-4 text-xl font-semibold tracking-tight text-fg">Sign in to TCM Search</h1>
          <p className="mt-1.5 text-sm text-muted">Test collection manuals from Australian labs, in one search.</p>
        </div>

        <div className="rounded-2xl border border-line bg-surface p-6 shadow-overlay sm:p-7">
          {message && !error && (
            <p className="mb-4 flex items-start gap-2 rounded-lg border border-info/30 bg-info/5 px-3 py-2.5 text-sm text-fg" role="status">
              <Info size={16} className="mt-0.5 shrink-0 text-info" aria-hidden="true" /> {message}
            </p>
          )}
          {error && (
            <p className="mb-4 flex items-start gap-2 rounded-lg border border-danger/30 bg-danger/5 px-3 py-2.5 text-sm text-fg" role="alert">
              <AlertTriangle size={16} className="mt-0.5 shrink-0 text-danger" aria-hidden="true" /> {error}
            </p>
          )}

          <form onSubmit={onSubmit} className="flex flex-col gap-4" noValidate>
            <div>
              <label htmlFor="login-username" className="mb-1.5 block text-sm font-medium text-fg-2">Username</label>
              <input
                id="login-username"
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="field h-10"
                autoComplete="username"
                autoCapitalize="none"
                spellCheck={false}
                autoFocus
                disabled={busy}
              />
            </div>
            <div>
              <label htmlFor="login-password" className="mb-1.5 block text-sm font-medium text-fg-2">Password</label>
              <div className="relative">
                <input
                  id="login-password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="field h-10 pr-11"
                  autoComplete="current-password"
                  disabled={busy}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  className="absolute right-1 top-1/2 flex h-8 w-9 -translate-y-1/2 items-center justify-center rounded-md text-faint hover:text-fg"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  aria-pressed={showPassword}
                >
                  {showPassword ? <EyeOff size={16} aria-hidden="true" /> : <Eye size={16} aria-hidden="true" />}
                </button>
              </div>
            </div>
            <Button type="submit" variant="primary" size="lg" loading={busy} className="mt-2 w-full">
              {busy ? 'Signing in…' : 'Sign in'}
            </Button>
          </form>
        </div>
        <p className="mt-6 text-center text-xs text-muted">Ask your administrator if you need an account.</p>
      </div>
    </main>
  );
};

export default LoginPage;
